import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import type { ImportTimeTolerance } from "@carry/shared";
import { useAuth } from "../providers/auth-context";
import { useAdminApi } from "../providers/api-context";

/**
 * 取込・一括補完の許容幅 (F23 システム設定) を取る (#754)。
 *
 * 受注一覧の一括補完は「片側だけの指定を許容幅で時間幅に埋める」処理なので、**許容幅を
 * 知らないと何件補完できるか数えられない**。許容幅 0 や日の端の丸めでは補完しても値が
 * 変わらず、押しても一覧の件数が減らない受注が残る。
 *
 * `useTimeSlotBoundaries` と**同じクエリキー**を使うので、両方を呼んでも通信は 1 回。
 * 取得できるまで null を返す。呼び出し側は件数を出さないこと (誤った件数を約束しない)。
 *
 * ここでは失敗を別扱いしない。**同じクエリキーの `useTimeSlotBoundaries` が理由と再試行を
 * `role="alert"` で出す**ので、設定取得に失敗した画面では「補完バナーが消えた理由」もそこに出る。
 * 両方が独立にエラー UI を出すと同じ失敗を二重に表示することになる。
 */
export function useImportTimeTolerance(): ImportTimeTolerance | null {
  const { session } = useAuth();
  const api = useAdminApi();
  const { data } = useQuery({
    queryKey: ["system-settings", session?.user?.id],
    queryFn: () => api.systemSettings.get(),
    enabled: !!session,
    staleTime: 5 * 60_000,
  });
  return useMemo<ImportTimeTolerance | null>(
    () =>
      data
        ? {
            earlyArrivalMinutes: data.data.import_early_arrival_tolerance_minutes,
            lateArrivalMinutes: data.data.import_late_arrival_tolerance_minutes,
          }
        : null,
    [data],
  );
}
